import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import axiosInstant from "../../../api";
import { getContentChannels, getContentTypes, pricingAll } from "../../../api/endpoints";
import { useUser } from "../../../hooks/useUser";
import { canEditChannelDefaults, canEditPricing } from "../../../utils";

export interface ContentType {
    id: number;
    name: string;
    deactivated?: boolean;
    expert_connect?: boolean;
    brands?: { id: number; name: string }[];
    content_channels?: { id: number; name: string }[];
}

export interface ContentChannel {
    id: number;
    name: string;
    deactivated?: boolean;
    content_types?: { id: number; name: string }[];
}

// Cost sheet row behind a content type base price or a package sell price.
// rate/cost are the BILL side (client-visible; the parent price is their
// sum); cost_rate/internal_cost are the INTERNAL side (margin tracking,
// never shown to clients). Flat items have null hours/rate.
export interface CostLineItem {
    id: number;
    content_type_price_id: number | null;
    pricing_package_id: number | null;
    label: string;
    hours: string | number | null;
    rate: string | number | null;
    cost: string | number;
    cost_rate: string | number | null;
    internal_cost: string | number | null;
    notes: string | null;
    sort_order: number;
}

export interface ContentTypePriceRow {
    id: number;
    content_type_id: number;
    base_price: string | number;
    per_product_base_price: string | number;
    content_type?: ContentType;
    line_items?: CostLineItem[];
}

export interface ContentTypeChannelPriceRow {
    id: number;
    content_type_id: number;
    content_channel_id: number;
    base_price: string | number;
    content_type?: ContentType;
    content_channel?: ContentChannel;
}

export interface LicenseRow {
    id: number;
    key: string;
    display_name: string;
    price: string | number;
}

export interface PackageRow {
    id: number;
    key: string;
    display_name: string;
    sell_price: string | number;
    max_skus: number;
    term_months: number | null;
    per_video_price: string | number | null;
    payment_schedule: string | null;
    deliverables: string | null;
    priority_scheduling: boolean;
    rollover: boolean;
    active: boolean;
    sort_order: number;
    line_items?: CostLineItem[];
}

export interface TierRow {
    id: number;
    discount_pct: string | number;
    min_projects?: number;
    min_distinct_projects?: number;
}

export interface PricingAllResponse {
    content_type_prices: ContentTypePriceRow[];
    content_type_channel_prices: ContentTypeChannelPriceRow[];
    licenses: LicenseRow[];
    packages: PackageRow[];
    project_count_tiers: TierRow[];
    matching_product_tiers: TierRow[];
}

interface PricingContextValue {
    data: PricingAllResponse | null;
    contentTypes: ContentType[];
    contentChannels: ContentChannel[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    /** Admin, account manager admin, and sales may edit; other internal roles view read-only. */
    canEdit: boolean;
    /** canEdit roles plus fulfillment (post production admin): may set channel option defaults only. */
    canEditChannelDefaults: boolean;
}

const PricingContext = createContext<PricingContextValue | null>(null);

export const usePricing = (): PricingContextValue => {
    const ctx = useContext(PricingContext);
    if (!ctx) throw new Error("usePricing must be used inside <PricingProvider>");
    return ctx;
};

export const PricingProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { role } = useUser();
    const [data, setData] = useState<PricingAllResponse | null>(null);
    const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
    const [contentChannels, setContentChannels] = useState<ContentChannel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [pricingRes, typesRes, channelsRes] = await Promise.all([
                axiosInstant.get(pricingAll),
                axiosInstant.get(getContentTypes()),
                axiosInstant.get(getContentChannels())
            ]);
            setData(pricingRes.data);
            setContentTypes(Array.isArray(typesRes.data) ? typesRes.data : typesRes.data?.rows ?? []);
            setContentChannels(
                Array.isArray(channelsRes.data) ? channelsRes.data : channelsRes.data?.rows ?? []
            );
        } catch (e: any) {
            setError(e?.response?.data?.error ?? "Failed to load pricing rules.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return (
        <PricingContext.Provider
            value={{
                data,
                contentTypes,
                contentChannels,
                loading,
                error,
                refetch,
                canEdit: canEditPricing(String(role)),
                canEditChannelDefaults: canEditChannelDefaults(String(role))
            }}
        >
            {children}
        </PricingContext.Provider>
    );
};
